import React, { useMemo } from "react";
import { StyleSheet } from "react-native";
import Reanimated from "react-native-reanimated";
import { useIsScreenSizeMin } from "../hooks/useIsScreenSizeMin";
import { useModalRecedeStyle } from "../hooks/use-modal-recede-style";
import { useTheme } from "../hooks/use-theme";
import {
    WEB_DIALOG_HEIGHT,
    WEB_DIALOG_MAX_HEIGHT,
    WEB_DIALOG_WIDTH,
} from "../utils/modal-stack-slot";

/**
 * The single source of truth for the centered web dialog box (700 / 85% / 900, rounded, shadowed),
 * shared by every navigation-modal wrapper AND the JS modals (components/ModalPortal.tsx). Change the
 * dialog's size/shape here once, everywhere.
 */
export const useModalDialogBoxStyle = () => {
    const { roundness } = useTheme();
    return useMemo(
        () =>
            StyleSheet.create({
                box: {
                    width: WEB_DIALOG_WIDTH,
                    maxWidth: "100%",
                    height: WEB_DIALOG_HEIGHT,
                    maxHeight: WEB_DIALOG_MAX_HEIGHT,
                    margin: "auto",
                    borderRadius: roundness * 3,
                    overflow: "hidden",
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore web-only shadow
                    boxShadow: "rgba(17, 12, 46, 0.15) 0px 48px 100px 0px",
                    backgroundColor: "white",
                },
            }).box,
        [roundness],
    );
};

/**
 * Wraps a navigation modal's content in the shared web dialog frame. On desktop web it is the animated
 * centered box that recedes (and hides) when another modal opens over it (via useModalRecedeStyle); on
 * native / mobile web it renders children as-is — the native modal / full-screen slide is the frame.
 *
 * Every navigation modal (ModalScreenWrapper, CreateMissionStack, the IJM/LPP/Vacation editors,
 * DocumentEditor) uses this so their box, stacking and recede stay identical and centralised.
 */
const ModalDialogFrame = ({ children }: { children: React.ReactNode }) => {
    const isLarge = useIsScreenSizeMin("large");
    const recedeStyle = useModalRecedeStyle();
    const boxStyle = useModalDialogBoxStyle();

    if (!isLarge) {
        return <>{children}</>;
    }
    return <Reanimated.View style={[boxStyle, recedeStyle]}>{children}</Reanimated.View>;
};

export default ModalDialogFrame;
